export interface ThemeColors {
  primary: string;
  primaryHover: string;
  primaryLight: string;
  primaryForeground?: string; // Text color on primary backgrounds
  secondary: string;
  secondaryHover: string;
  secondaryForeground?: string; // Text color on secondary backgrounds
  accent: string;
  accentHover: string;
  accentForeground?: string; // Text color on accent backgrounds
  background: string;
  foreground: string;
  muted: string;
  mutedForeground: string;
  border: string;
  input: string;
  ring: string;
  destructive: string;
  destructiveForeground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  sidebarColor: string;
  sidebarTextColor: string;
  sidebarHoverBgColor: string;
  sidebarHoverTextColor: string;
}

export interface SEOConfig {
  pageTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  robotsMeta?: string;
}

export interface ThemeConfig {
  id: string;
  name: string;
  description: string;
  colors: ThemeColors;
  logo?: string;
  favicon?: string;
  seo?: SEOConfig;
  isDefault?: boolean;
  isCustom?: boolean;
  createdBy?: string;
  createdAt?: string;
}

export interface ThemeSettings {
  currentTheme: string;
  customLogo?: string;
  customFavicon?: string;
  customSEO?: SEOConfig;
  themes: ThemeConfig[];
  customThemes: ThemeConfig[];
  sidebarColorOverride?: string | null;
  darkMode?: boolean;
}

export const DEFAULT_THEME: ThemeConfig = {
  id: 'joules-green',
  name: 'Joules Green',
  description: 'Default Joules theme with green and blue accents',
  isDefault: true,
  isCustom: false,
  colors: {
    primary: '#10B981',
    primaryHover: '#0d9d6d',
    primaryLight: '#e7f8f2',
    primaryForeground: '#000000',
    secondary: '#3B82F6',
    secondaryHover: '#326ed1',
    secondaryForeground: '#ffffff',
    accent: '#059669',
    accentHover: '#047f59',
    accentForeground: '#ffffff',
    background: '#F9FAFB',
    foreground: '#1F2937',
    muted: '#f5f5f5',
    mutedForeground: '#000000',
    border: '#e5e7eb',
    input: '#ffffff',
    ring: '#10B981',
    destructive: '#ef4444',
    destructiveForeground: '#ffffff',
    card: '#ffffff',
    cardForeground: '#000000',
    popover: '#ffffff',
    popoverForeground: '#000000',
    sidebarColor: '#1f2937',
    sidebarTextColor: '#ffffff',
    sidebarHoverBgColor: '#1a222e',
    sidebarHoverTextColor: '#ffffff',
  },
  logo: '/joules-logo.png',
  favicon: '/joules-logo.png',
  seo: {
    pageTitle: 'Joules Project Management',
    metaDescription: 'Professional solar project management platform for installers and project managers.',
    metaKeywords: 'solar, project management, installer, renewable energy, solar installation',
    ogTitle: 'Joules Project Management',
    ogDescription: 'Professional solar project management platform for installers and project managers.',
    ogImage: '/joules-logo.png',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Joules Project Management',
    twitterDescription: 'Professional solar project management platform for installers and project managers.',
    twitterImage: '/joules-logo.png',
    canonicalUrl: '',
    robotsMeta: 'index, follow',
  },
};

export const PRESET_THEMES: ThemeConfig[] = [
  DEFAULT_THEME,
  {
    id: 'joules-gold',
    name: 'Joules Gold',
    description: 'Classic Joules theme with gold accents',
    colors: {
      primary: '#BEA04D',
      primaryHover: '#A38A41',
      primaryLight: '#F3ECD9',
      primaryForeground: '#000000',
      secondary: '#CC9C00',
      secondaryHover: '#B38A00',
      secondaryForeground: '#000000',
      accent: '#F3ECD9',
      accentHover: '#E8DCC0',
      accentForeground: '#000000',
      background: '#ffffff',
      foreground: '#000000',
      muted: '#f5f5f5',
      mutedForeground: '#6b7280',
      border: '#e5e7eb',
      input: '#ffffff',
      ring: '#BEA04D',
      destructive: '#ef4444',
      destructiveForeground: '#ffffff',
      card: '#ffffff',
      cardForeground: '#000000',
      popover: '#ffffff',
      popoverForeground: '#000000',
      sidebarColor: '#000000',
      sidebarTextColor: '#ffffff',
      sidebarHoverBgColor: '#374151',
      sidebarHoverTextColor: '#ffffff',
    },
    seo: {
      pageTitle: 'Joules Project Management',
      metaDescription: 'Professional solar project management platform for installers and project managers.',
      metaKeywords: 'solar, project management, installer, renewable energy, solar installation',
      ogTitle: 'Joules Project Management',
      ogDescription: 'Professional solar project management platform for installers and project managers.',
      ogImage: '/joules-logo.png',
      twitterCard: 'summary_large_image',
      twitterTitle: 'Joules Project Management',
      twitterDescription: 'Professional solar project management platform for installers and project managers.',
      twitterImage: '/joules-logo.png',
      canonicalUrl: '',
      robotsMeta: 'index, follow',
    },
  },
  {
    id: 'joules-blue',
    name: 'Joules Blue',
    description: 'Professional blue theme',
    colors: {
      primary: '#2563eb',
      primaryHover: '#1d4ed8',
      primaryLight: '#dbeafe',
      primaryForeground: '#ffffff', // White text on blue background
      secondary: '#1e40af',
      secondaryHover: '#1e3a8a',
      secondaryForeground: '#ffffff', // White text on dark blue background
      accent: '#dbeafe',
      accentHover: '#bfdbfe',
      accentForeground: '#1e40af', // Dark blue text on light blue background
      background: '#ffffff',
      foreground: '#000000',
      muted: '#f8fafc',
      mutedForeground: '#64748b',
      border: '#e2e8f0',
      input: '#ffffff',
      ring: '#2563eb',
      destructive: '#ef4444',
      destructiveForeground: '#ffffff',
      card: '#ffffff',
      cardForeground: '#000000',
      popover: '#ffffff',
      popoverForeground: '#000000',
      sidebarColor: '#000000', // Black sidebar
      sidebarTextColor: '#ffffff', // White text on black sidebar
      sidebarHoverBgColor: '#374151', // Dark gray hover background
      sidebarHoverTextColor: '#ffffff', // White text on hover
    },
    seo: {
      pageTitle: 'Joules Project Management',
      metaDescription: 'Professional solar project management platform for installers and project managers.',
      metaKeywords: 'solar, project management, installer, renewable energy, solar installation',
      ogTitle: 'Joules Project Management',
      ogDescription: 'Professional solar project management platform for installers and project managers.',
      ogImage: '/joules-logo.png',
      twitterCard: 'summary_large_image',
      twitterTitle: 'Joules Project Management',
      twitterDescription: 'Professional solar project management platform for installers and project managers.',
      twitterImage: '/joules-logo.png',
      canonicalUrl: '',
      robotsMeta: 'index, follow',
    },
  },
  {
    id: 'joules-purple',
    name: 'Joules Purple',
    description: 'Modern purple theme',
    colors: {
      primary: '#7c3aed',
      primaryHover: '#6d28d9',
      primaryLight: '#ede9fe',
      primaryForeground: '#ffffff', // White text on purple background
      secondary: '#5b21b6',
      secondaryHover: '#4c1d95',
      secondaryForeground: '#ffffff', // White text on dark purple background
      accent: '#ede9fe',
      accentHover: '#ddd6fe',
      accentForeground: '#5b21b6', // Dark purple text on light purple background
      background: '#ffffff',
      foreground: '#000000',
      muted: '#faf5ff',
      mutedForeground: '#6b7280',
      border: '#e9d5ff',
      input: '#ffffff',
      ring: '#7c3aed',
      destructive: '#ef4444',
      destructiveForeground: '#ffffff',
      card: '#ffffff',
      cardForeground: '#000000',
      popover: '#ffffff',
      popoverForeground: '#000000',
      sidebarColor: '#000000', // Black sidebar
      sidebarTextColor: '#ffffff', // White text on black sidebar
      sidebarHoverBgColor: '#374151', // Dark gray hover background
      sidebarHoverTextColor: '#ffffff', // White text on hover
    },
    seo: {
      pageTitle: 'Joules Project Management',
      metaDescription: 'Professional solar project management platform for installers and project managers.',
      metaKeywords: 'solar, project management, installer, renewable energy, solar installation',
      ogTitle: 'Joules Project Management',
      ogDescription: 'Professional solar project management platform for installers and project managers.',
      ogImage: '/joules-logo.png',
      twitterCard: 'summary_large_image',
      twitterTitle: 'Joules Project Management',
      twitterDescription: 'Professional solar project management platform for installers and project managers.',
      twitterImage: '/joules-logo.png',
      canonicalUrl: '',
      robotsMeta: 'index, follow',
    },
  },
]; 